import { Injectable, OnDestroy } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { Observable, BehaviorSubject, of, Subscription, Subject } from 'rxjs';
import { map, catchError, switchMap, finalize } from 'rxjs/operators';
import { UserModel, NewUserModel, UserLoginModel} from '../_models/user.model';
import { AuthModel, PassChange } from '../_models/auth.model';
import { AuthHTTPService } from './auth-http';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';

//import _ from 'highlight.js/lib/languages/*';

const jwt = new JwtHelperService();

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {

  // private fields
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/
  private authLocalStorageToken = `${environment.appVersion}-${environment.USERDATA_KEY}`;

  // public fields
  permissionMenu$: Observable<any>;
  currentUser$: Observable<UserModel>;
  isLoading$: Observable<boolean>;
  currentUserSubject: BehaviorSubject<UserModel>;
  permissionMenuSubject: BehaviorSubject<{}>;
  //permissionMenuSubject =  new Subject<any>();

  isLoadingSubject: BehaviorSubject<boolean>;
  auth: any;
  httpHeaders: HttpHeaders;

  get currentUserValue(): UserModel {
    return this.currentUserSubject.value;
  }

  set currentUserValue(user: UserModel) {
    this.currentUserSubject.next(user);
  }

  get permissionMenu() {
    return this.permissionMenuSubject.value;
  }

  constructor(
    private authHttpService: AuthHTTPService,
    private router: Router

  ) {
    this.initHttHeaders();

    this.isLoadingSubject = new BehaviorSubject<boolean>(false);
    this.currentUserSubject = new BehaviorSubject<UserModel>(undefined);
    this.permissionMenuSubject = new BehaviorSubject<any>(undefined);

    this.currentUser$ = this.currentUserSubject.asObservable();
    this.isLoading$ = this.isLoadingSubject.asObservable();
    this.permissionMenu$ = this.permissionMenuSubject.asObservable();
    const subscr = this.getUserByToken().subscribe();
    this.unsubscribe.push(subscr);

  }

  token() {
    return this.getAuthFromLocalStorage().accessToken || "";
  }

  decodeToken(accessToken) {
    return jwt.decodeToken(accessToken || "");
  }
  
  initHttHeaders() {
    try {
      this.httpHeaders =  new HttpHeaders({
        Authorization: `Bearer ` + this.token(),
      });
    }
    catch{
      console.log("Login invalido");
    }
  }


  // public methods
  login(code: string, codelogin: string): Observable<UserModel> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.login(code, codelogin).pipe(
      map(async (_auth: any) => {
        
        const token =  this.decodeToken(_auth.jwtToken);
        this.auth = { 
          id: _auth.jwtToken,
          accessToken: _auth.jwtToken,
          refreshToken: _auth.refreshToken,
          expiresIn: new Date(token.exp * 1000), //new Date(Date.now() + 100 * 24 * 60 * 60 * 1000)
          role: token.role,
          primarygroupsid: token.primarygroupsid,
          primarysid: token.primarysid
        };
        const result = this.setAuthFromLocalStorage(this.auth);
        const permissionMenu = localStorage.setItem("permissionMenu", JSON.stringify( [_auth.permisoMenu] ));

        this.permissionMenuSubject = new BehaviorSubject<any>([_auth.permisoMenu][0]);
        return result;
      }),
      switchMap(() => this.getUserByToken()),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  logout(message = "") {
    localStorage.removeItem(this.authLocalStorageToken);
    this.router.navigate(['/auth/login?user=invalid'], {
      queryParams: {},
    });
  }

  getUserByToken(): Observable<UserModel> {
    const auth = this.getAuthFromLocalStorage();

    if (!auth || !auth.accessToken) {
      return of(undefined);
    }

    this.isLoadingSubject.next(true);
    var user : any;
    this.httpHeaders =  new HttpHeaders({
      Authorization: `Bearer ` + auth.accessToken,
    });
    return this.authHttpService.getUserByToken(this.httpHeaders).pipe(
      map((_user: any) => {
        
        if (_user[0]) {
          var fullname = _user[0].NombreCompleto.split(' ');
          user = {
            role : parseInt(auth.role.toString()),
            id : _user[0].IdVinculo,
            fullname : _user[0].NombreCompleto,
            firstname : fullname[0] || _user[0].NombreCompleto,
            lastname : fullname[1] || '',
            vinculo : _user[0].Vinculo,
            isAdmin: _user[0].IdEsAdministrador,
            email: _user[0].Email,
            phone: _user[0].Telefono,
            cedula: _user[0].Cedula
          };
          this.currentUserSubject = new BehaviorSubject<UserModel>(user);
          this.permissionMenuSubject = new BehaviorSubject<any>( JSON.parse(localStorage.getItem("permissionMenu"))[0] )
        } else {
          this.logout();
        }
        return user;
      }),
      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  // need create new user then login
  registration(user: NewUserModel): Observable<any> {
    this.isLoadingSubject.next(true);
    return this.authHttpService.createUser(user).pipe(
      map((response) => {
        this.isLoadingSubject.next(false);
        console.log(response);
        return response;
      }),
      //switchMap(() => this.login(user.email, user.password)),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),

      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  // need create new user then login
  createUserMode2(user: any): Observable<any> {
    this.isLoadingSubject.next(true);
    var form = new FormData();
    form.append("Usuario", user.Email);
    form.append("Clave", user.Clave);
    return this.authHttpService.createUserMode2(form, this.httpHeaders).pipe(
      map((response) => {
        this.isLoadingSubject.next(false);
        console.log(response);
        return response;
      }),
      //switchMap(() => this.login(user.email, user.password)),
      catchError((err) => {
        console.error('err', err);
        return of(undefined);
      }),

      finalize(() => this.isLoadingSubject.next(false))
    );
  }

  forgotPassword(email: string): Observable<PassChange> {
    this.isLoadingSubject.next(true);
    return this.authHttpService
      .forgotPassword(email)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  forgotPasswordMode2(email: string = ""): Observable<PassChange> {
    this.isLoadingSubject.next(true);
    return this.authHttpService
      .forgotPasswordMode2(this.httpHeaders)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  changePassword(code: string, email:string, password: string): Observable<PassChange> {
    var data = new FormData();
    data.append("usuario", email);
    data.append("clave", password);
    this.isLoadingSubject.next(true);
    return this.authHttpService
      .changePassword(data)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  changePasswordMode2(code: string, email:string, password: string): Observable<PassChange> {
    var data = new FormData();
    data.append("usuario", email);
    data.append("clave", password);

    this.isLoadingSubject.next(true);
    return this.authHttpService
      .changePasswordMode2(data, this.httpHeaders)
      .pipe(finalize(() => this.isLoadingSubject.next(false)));
  }

  // private methods
  private setAuthFromLocalStorage(auth: AuthModel): boolean {
    // store auth accessToken/refreshToken/epiresIn in local storage to keep user logged in between page refreshes
    if (auth && auth.accessToken) {
      localStorage.setItem(this.authLocalStorageToken, JSON.stringify(auth));
      return true;
    }
    return false;
  }

  private getAuthFromLocalStorage(): AuthModel {
      try {
        const authData = JSON.parse(
          localStorage.getItem(this.authLocalStorageToken)
        );
        return authData;
      } catch (error) {
        console.error(error);
      return undefined;
    }
  }

  

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }
}
